/* ==========================================================================
   Home, collection and header behaviour.

   Two widgets:
     <hp-rail>    the scroll-snap row behind the video and review carousels.
                  The scrolling itself is the browser's; this only wires the
                  two arrows and greys them out at the ends.
     <hp-header>  the nav drawer and the reveal-on-scroll-up bar.
     <hp-market>  submits the footer's country form once a country is picked.

   Several sections ask for this file, and a page carrying more than one of
   them runs it more than once, so every definition is guarded. Without the
   guard the second run throws on the first customElements.define and the
   widgets after it are never defined at all.
   ========================================================================== */

class HpRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.hp-rail__track');
    if (!this.track) return;

    this.prev = this.querySelector('.hp-rail__arrow--prev');
    this.next = this.querySelector('.hp-rail__arrow--next');

    if (this.prev) this.prev.addEventListener('click', () => this.page(-1));
    if (this.next) this.next.addEventListener('click', () => this.page(1));

    this.onScroll = () => this.syncArrows();
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.syncArrows();
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
  }

  /* One card plus its gap, so a click lands on the next snap point. */
  step() {
    const item = this.querySelector('.hp-rail__item');
    if (!item) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  page(direction) {
    this.track.scrollBy({ left: this.step() * direction, behavior: 'smooth' });
  }

  syncArrows() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    /* A pixel of slack: fractional widths never land exactly on the end. */
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max - 1;
  }
}
if (!customElements.get('hp-rail')) customElements.define('hp-rail', HpRail);

class HpHeader extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.hp-header__bar');
    this.drawer = this.querySelector('.hp-nav');
    this.burger = this.querySelector('.hp-header__burger');

    if (this.burger && this.drawer) {
      this.burger.addEventListener('click', () => this.openNav());
      this.drawer.querySelectorAll('[data-nav-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeNav());
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeNav();
      });
    }

    if (this.hasAttribute('data-sticky')) {
      this.lastY = window.scrollY;
      this.onScroll = () => this.sync();
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
  }

  openNav() {
    this.drawer.hidden = false;
    /* One frame before the class, or the panel has no start position to
       transition from and simply appears. */
    requestAnimationFrame(() => this.drawer.classList.add('hp-nav--open'));
    this.burger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    this.drawer.querySelector('.hp-nav__close')?.focus();
  }

  closeNav() {
    if (this.drawer.hidden) return;
    this.drawer.classList.remove('hp-nav--open');
    this.burger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
    this.burger.focus();
    const hide = () => {
      this.drawer.hidden = true;
    };
    this.drawer.addEventListener('transitionend', hide, { once: true });
    /* transitionend never fires when the panel was never painted, so fall
       back rather than leaving an invisible overlay swallowing clicks. */
    setTimeout(hide, 400);
  }

  sync() {
    const y = window.scrollY;
    const height = this.bar.offsetHeight;

    if (y <= height) {
      this.classList.remove('hp-header--stuck', 'hp-header--opening');
    } else {
      this.classList.add('hp-header--stuck');
      this.classList.toggle('hp-header--opening', y < this.lastY);
    }
    this.lastY = y;
  }
}
if (!customElements.get('hp-header')) customElements.define('hp-header', HpHeader);

class HpMarket extends HTMLElement {
  connectedCallback() {
    const select = this.querySelector('select[name="country_code"]');
    const form = this.querySelector('form');
    if (!select || !form) return;
    select.addEventListener('change', () => form.submit());
  }
}
if (!customElements.get('hp-market')) customElements.define('hp-market', HpMarket);
